// React Dependencies
import React, { useState, useEffect, useReducer } from 'react';

// External Dependencies
import PropTypes from 'prop-types';
import { Typography, Button, TextareaAutosize, RadioGroup, FormControlLabel, Radio } from '@material-ui/core';
import { toast } from 'react-toastify';

// Internal Dependencies
import AreaMap from '../../../areaMap/areaMap';
import apiService from '../../../../services.js/apiService';
import Colours from '../../../../styles/colours';
import OverLoading from '../../../global/OverLoading';
import ColorPickerInput from '../../luckyDraws/details/ColorPickerInput';
import { IsEmpty, IsValidHexColor } from '../../../../common/checks';
import { Status } from '../../../../common/constants';
import { Trim } from '../../../../common/util';

const defaultColour = Colours.Red.slice(1);
const ColourSetOptions = { NoColour: 'No colour', SetColour: 'Set colour' };
const defaultMapBoundConfig = { calloutColour: null };

const Boundaries = ({ match }) => {
  const { pageId, trailId } = match.params;

  const [isLoading, setIsLoading] = useState(false);
  const [isDeactivated, setIsDeactivated] = useState(false);
  const [isMapBoundColourSet, setIsMapBoundColourSet] = useState(false);
  const [isCalloutColourSet, setIsCalloutColourSet] = useState(false);
  const [mapBoundData, setMapBoundData] = useState(null);
  const [coords, setCoords] = useState([]);
  const [calloutTitle, setCalloutTitle] = useState('');
  const [calloutContent, setCalloutContent] = useState('');
  const [calloutCoord, setCalloutCoord] = useState({});
  const [isMapBoundColourPickerOpen, setIsMapBoundColourPickerOpen] = useState(false);
  const [isCalloutColourPickerOpen, setIsCalloutColourPickerOpen] = useState(false);
  const [mapBoundColour, setMapBoundColour] = useState(null);
  const [mapBoundConfig, setMapBoundConfig] = useReducer((state, action) => ({ ...state, ...action }), defaultMapBoundConfig);

  const displayedMapBoundColour = `#${
    (isMapBoundColourSet && !IsEmpty(mapBoundColour) && IsValidHexColor(mapBoundColour)) ? mapBoundColour : defaultColour}`;

  const displayedCalloutColour = `#${
    (isCalloutColourSet && !IsEmpty(mapBoundConfig.calloutColour) && IsValidHexColor(mapBoundConfig.calloutColour))
      ? mapBoundConfig.calloutColour : defaultColour}`;

  const getTrailBoundaries = async (mapBound = null) => {
    try {
      setIsLoading(true);

      if (IsEmpty(mapBound)) {
        mapBound = await apiService.getTrailMapBound(trailId);

        const trail = await apiService.getTrail(pageId, trailId);
        setIsDeactivated(trail.status === Status.Deactivated);
      }

      if (!IsEmpty(mapBound)) {
        setCoords(mapBound.coords || []);
        setMapBoundData(mapBound);
        setCalloutTitle(mapBound.calloutTitle);
        setCalloutContent(mapBound.calloutContent);
        setCalloutCoord(!IsEmpty(mapBound.calloutCoord) ? mapBound.calloutCoord : {});

        setMapBoundColour(!IsEmpty(mapBound.colour) ? mapBound.colour : null);
        setIsMapBoundColourSet(!IsEmpty(mapBound.colour));

        setMapBoundConfig((!IsEmpty(mapBound.config) && !IsEmpty(mapBound.config.calloutColour)) ? mapBound.config : defaultMapBoundConfig);
        setIsCalloutColourSet(!IsEmpty(mapBound.config) && !IsEmpty(mapBound.config.calloutColour));
      }
    } catch (error) {
      toast.error('Error while fetching data for trail boundaries');
      console.log('Error in getTrailBoundaries: ', error);
    } finally {
      setIsLoading(false);
    }
  };

  const onSaveHandler = async () => {
    try {
      setIsLoading(true);

      const trimmedCalloutTitle = Trim(calloutTitle);
      if (trimmedCalloutTitle.split('\n').length > 2) {
        return toast.error('Callout title should not exceed 2 lines');
      }

      const payload = {
        coords,
        calloutTitle,
        calloutContent,
        calloutCoord,
        colour:  mapBoundColour,
        trailId: !IsEmpty(trailId) ? Number(trailId) : null,
        config:  mapBoundConfig,
      };

      let latestData = null;
      if (IsEmpty(mapBoundData)) { // CREATE
        latestData = await apiService.createTrailMapBound(trailId, payload);
      } else { // UPDATE
        payload.id = mapBoundData.id;
        latestData = await apiService.updateTrailMapBound(trailId, payload);
      }

      await getTrailBoundaries(latestData);
      toast.success('Map boundaries successfully saved');
    } catch (error) {
      toast.error(error.message);
      console.log('Error in onSaveHandler: ', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    getTrailBoundaries();
  }, []);

  useEffect(() => {
    if (!isMapBoundColourSet) {
      setMapBoundColour(null);
    } else if (isMapBoundColourSet && IsEmpty(mapBoundColour)) {
      setMapBoundColour(defaultColour);
    }
  }, [isMapBoundColourSet]);

  useEffect(() => {
    if (!isCalloutColourSet) {
      setMapBoundConfig(defaultMapBoundConfig);
    } else if (isCalloutColourSet && IsEmpty(mapBoundConfig.calloutColour)) {
      setMapBoundConfig({ calloutColour: defaultColour });
    }
  }, [isCalloutColourSet]);

  if (isLoading) {
    return <OverLoading />;
  }

  return (
    <div>
      {isLoading && <OverLoading />}
      <Typography variant="h5" style={{ fontWeight: 'bold' }}>Map Boundary</Typography>

      <Typography className="mt-2" variant="subtitle1">Fill Colour</Typography>
      <RadioGroup
        row
        value={isMapBoundColourSet ? ColourSetOptions.SetColour : ColourSetOptions.NoColour}
        onChange={({ target: { value } }) => setIsMapBoundColourSet(value === ColourSetOptions.SetColour)}

      >
        {Object.values(ColourSetOptions).map((value) => (
          <div key={value} className="mr-3">
            <FormControlLabel
              value={value}
              control={<Radio disabled={isDeactivated} color="secondary" />}
              style={{ fontSize: 13 }}
              label={value}
            />
          </div>
        ))}
      </RadioGroup>

      {isMapBoundColourSet && (
        <ColorPickerInput
          isOpen={isMapBoundColourPickerOpen}
          colour={displayedMapBoundColour}
          onChange={(value) => (!IsEmpty(value) ? setMapBoundColour(value.slice(1)) : null)}
          onClose={() => setIsMapBoundColourPickerOpen(false)}
          onButtonClick={() => setIsMapBoundColourPickerOpen((prevState) => !prevState)}
        />
      )}

      <AreaMap
        disabled={isDeactivated}
        colour={displayedMapBoundColour}
        style={{ padding: 0 }}
        buttonContainerStyles={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap' }}
        buttonSubContainerStyle={{ display: 'flex', justifyContent: 'flex-end' }}
        Heading={() => (
          <Typography variant="subtitle1" color="inherit">
            Click region in the map to set the trail&apos;s boundary
          </Typography>
        )}
        setMapCoor={(c) => setCoords(c)}
        mapCoordinates={coords}
      />

      <hr className="my-4" />

      <div className="w-100 d-flex flex-column" style={{ gap: '0.5rem' }}>
        <Typography variant="h5" style={{ fontWeight: 'bold' }}>Callout Settings</Typography>

        <div className="w-100 d-flex flex-column mt-2">
          <Typography variant="subtitle1">Fill Colour</Typography>
          <RadioGroup
            row
            value={isCalloutColourSet ? ColourSetOptions.SetColour : ColourSetOptions.NoColour}
            onChange={({ target: { value } }) => setIsCalloutColourSet(value === ColourSetOptions.SetColour)}
          >
            {Object.values(ColourSetOptions).map((value) => (
              <div key={value} className="mr-3">
                <FormControlLabel
                  value={value}
                  control={<Radio disabled={isDeactivated} color="secondary" />}
                  style={{ fontSize: 13 }}
                  label={value}
                />
              </div>
            ))}
          </RadioGroup>

          {isCalloutColourSet && (
            <ColorPickerInput
              isOpen={isCalloutColourPickerOpen}
              colour={displayedCalloutColour}
              onChange={(value) => (!IsEmpty(value) ? setMapBoundConfig({ calloutColour: value.slice(1) }) : null)}
              onClose={() => setIsCalloutColourPickerOpen(false)}
              onButtonClick={() => setIsCalloutColourPickerOpen((prevState) => !prevState)}
            />
          )}

          <div className="w-100 mt-3 pt-2">
            <Typography variant="subtitle1">Enter title (Max 2 lines and 100 characters)</Typography>
            <TextareaAutosize
              disabled={isDeactivated}
              rowsMax={2}
              style={{
                fontSize:     '13px',
                width:        '100%',
                borderRadius: '4px',
                padding:      '12px',
              }}
              variant="outlined"
              maxLength={100}
              value={calloutTitle}
              onChange={(e) => setCalloutTitle(e.target.value)}
              onKeyDown={(e) => {
                const text = e.target.value;
                const rows = text.split('\n').length;
                if (rows >= 2 && e.key === 'Enter') {
                  e.preventDefault(); // Prevent the default behavior if the input already has 2 lines of text
                }
              }}
            />
          </div>

          <div className="w-100 mt-2">
            <Typography variant="subtitle1">Enter content (Max 150 characters)</Typography>
            <TextareaAutosize
              disabled={isDeactivated}
              variant="outlined"
              rowsMax={4}
              maxLength={150}
              style={{
                fontSize:     '13px',
                width:        '100%',
                borderRadius: '4px',
                padding:      '12px',
              }}
              value={calloutContent}
              onChange={(e) => setCalloutContent(e.target.value)}
            />
            { !IsEmpty(calloutContent) && String(calloutContent).length > 150 && (
              <Typography style={{ color: Colours.Red }}>
                Content Length=
                {String(calloutContent).length}
              </Typography>
            )}
          </div>
        </div>

        <div className="w-100 mt-3">
          <Typography variant="h6">Callout Placement</Typography>
          <AreaMap
            disabled={isDeactivated}
            style={{ padding: 0 }}
            buttonContainerStyles={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap' }}
            buttonSubContainerStyle={{ display: 'flex', justifyContent: 'flex-end' }}
            Heading={() => (
              <Typography variant="subtitle1" color="inherit">
                Click a point in the map to set the the callout&apos;s location
              </Typography>
            )}
            colour={displayedMapBoundColour}
            defaultColour={`#${defaultColour}`}
            isCalloutCoord
            setCalloutCoord={(c) => setCalloutCoord(c)}
            calloutCoord={calloutCoord}
            title={calloutTitle}
            content={calloutContent}
            mapCoordinates={coords}
            calloutColour={displayedCalloutColour}
          />
        </div>
      </div>
      <Button disabled={isDeactivated} color="primary" onClick={onSaveHandler} className="mt-4" variant="contained">Save</Button>
    </div>
  );
};

Boundaries.propTypes = {
  match: PropTypes.shape(Object).isRequired,
};

export default Boundaries;
